import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import {
  Leaf, Rocket, BookMarked, Clock, Inbox, CheckCircle2,
  AlertTriangle, Edit3, Megaphone, Loader2, ArrowLeft,
  ExternalLink, ShieldCheck, Calendar,
} from 'lucide-react';

/**
 * /predict/validation-log — Public news-feed timeline of every validation-
 * relevant event in the Predict module. Anonymous-accessible (no auth gate)
 * so LinkedIn click-throughs land directly. Visual grammar matches /about.
 */

const CREAM = '#FAF6EE';
const PAPER = '#FFFFFF';
const INK = '#1F3A2E';
const SAGE = '#2D6A4F';
const SAGE_MUTED = '#7A8B7A';
const CHARCOAL = '#2C2C2C';
const FONT_HEADING = "'Cormorant Garamond', Georgia, serif";
const FONT_BODY = "'Inter', 'DM Sans', system-ui, sans-serif";

const EVENT_TYPE_CONFIG = {
  shipped: { icon: Rocket, label: 'Shipped', color: '#1B5E20', bg: '#E6F4EA', border: '#A5D6A7' },
  'methodology-update': { icon: BookMarked, label: 'Methodology', color: '#2E3580', bg: '#E8EAF6', border: '#B5BCE0' },
  'pilot-pending': { icon: Clock, label: 'Pilot pending', color: '#7C5800', bg: '#FFF8E1', border: '#FFE082' },
  'pilot-submitted': { icon: Inbox, label: 'Pilot submitted', color: '#2E3580', bg: '#E8EAF6', border: '#B5BCE0' },
  'pilot-passed': { icon: CheckCircle2, label: 'Pilot passed', color: '#1B5E20', bg: '#C8E6C9', border: '#81C784' },
  'pilot-failed': { icon: AlertTriangle, label: 'Pilot failed', color: '#8B1A4A', bg: '#FCE4EC', border: '#F8BBD0' },
  'data-corrected': { icon: Edit3, label: 'Data corrected', color: '#01579B', bg: '#E1F5FE', border: '#81D4FA' },
  announcement: { icon: Megaphone, label: 'Announcement', color: '#92400E', bg: '#FFF3E0', border: '#FFCC80' },
};

const FILTERS = [
  { key: null, label: 'All' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'methodology-update', label: 'Methodology' },
  { key: 'pilot-pending', label: 'Pilots' },
  { key: 'announcement', label: 'Announcements' },
];

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d)) return '';
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch {
    return '';
  }
}

export default function ValidationLog() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = filter ? { event_type: filter, limit: 200 } : { limit: 200 };
    axios.get(`${API}/predict/validation-log`, { params })
      .then(r => { if (alive) { setData(r.data); setError(null); } })
      .catch(e => { if (alive) setError(e?.response?.data?.detail || 'Validation log fetch failed.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- API stable; setters stable
  }, [filter]);

  // Derive events + count from `data` on every render — both are
  // cheap pass-throughs so we don't memoize them here. groupedByDate
  // below depends on `data` directly so it doesn't get tripped by
  // the `events` reference identity changing each render.
  const events = data?.events || [];
  const eventCount = data?.count || 0;

  const groupedByDate = useMemo(() => {
    const groups = {};
    (data?.events || []).forEach(e => {
      const d = fmtDate(e.timestamp);
      if (!groups[d]) groups[d] = [];
      groups[d].push(e);
    });
    return groups;
  }, [data]);

  return (
    <div
      style={{ background: CREAM, minHeight: '100vh', fontFamily: FONT_BODY, color: CHARCOAL }}
      data-testid="validation-log-page"
    >
      {/* Public top bar */}
      <header
        className="flex items-center justify-between px-6 md:px-10 py-5"
        style={{ borderBottom: '1px solid rgba(31, 58, 46, 0.08)' }}
      >
        <Link to="/" className="flex items-center gap-2.5 select-none" data-testid="vlog-logo">
          <Leaf size={22} style={{ color: SAGE }} strokeWidth={2.2} />
          <span style={{ fontFamily: FONT_HEADING, fontSize: 22, color: INK, letterSpacing: '-0.01em' }}>
            BKAgriD
          </span>
        </Link>
        <div className="flex items-center gap-5">
          <Link to="/predict" style={navLinkStyle} data-testid="vlog-nav-predict">Predict</Link>
          <Link to="/predict/validation" style={navLinkStyle} data-testid="vlog-nav-validation">Validation</Link>
          <Link to="/about" style={navLinkStyle} data-testid="vlog-nav-about">About</Link>
          <button
            onClick={() => navigate('/login')}
            style={{
              fontSize: 13, fontWeight: 600, color: PAPER, background: SAGE, border: 'none',
              padding: '8px 16px', borderRadius: 6, cursor: 'pointer',
            }}
            data-testid="vlog-nav-signin"
          >
            Sign in
          </button>
        </div>
      </header>

      <main className="max-w-[860px] mx-auto px-6 md:px-10 py-12 md:py-20">
        <button
          onClick={() => navigate('/predict/validation')}
          className="inline-flex items-center gap-1.5 mb-8 transition-colors"
          style={{ fontSize: 13, color: SAGE_MUTED, background: 'transparent', border: 'none', cursor: 'pointer' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = SAGE; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = SAGE_MUTED; }}
          data-testid="vlog-back"
        >
          <ArrowLeft size={14} /> Back to Validation Dashboard
        </button>

        {/* Hero */}
        <section className="mb-10" data-testid="vlog-hero">
          <div className="inline-flex items-center gap-2 mb-4" style={{ color: SAGE, fontSize: 11, fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
            <ShieldCheck size={13} /> Public validation log
          </div>
          <h1
            style={{
              fontFamily: FONT_HEADING, fontStyle: 'italic',
              fontSize: 52, lineHeight: 1.06, color: INK,
              fontWeight: 500, letterSpacing: '-0.015em', marginBottom: 14, maxWidth: 720,
            }}
            data-testid="vlog-headline"
          >
            Every change, on the record.
          </h1>
          <p style={{ fontSize: 16, lineHeight: 1.7, color: CHARCOAL, fontWeight: 300, maxWidth: 680 }}>
            Append-only, time-stamped public timeline of every meaningful change to the BKAgriD
            Predict module — code shipped, methodology updated, pilots submitted, data corrected,
            announcements made. The dashboard exists before the predictions. This log keeps it
            honest.
          </p>
        </section>

        {/* Filters */}
        <section className="mb-8" data-testid="vlog-filters">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map(f => (
              <button
                key={f.key || 'all'}
                onClick={() => setFilter(f.key)}
                style={{
                  fontSize: 12, fontWeight: 600,
                  color: filter === f.key ? PAPER : CHARCOAL,
                  background: filter === f.key ? SAGE : PAPER,
                  border: `1px solid ${filter === f.key ? SAGE : 'rgba(31, 58, 46, 0.15)'}`,
                  padding: '7px 14px', borderRadius: 999, cursor: 'pointer',
                  letterSpacing: '0.02em',
                }}
                data-testid={`vlog-filter-${f.key || 'all'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <p style={{ fontSize: 12, color: SAGE_MUTED, marginTop: 10, fontStyle: 'italic' }}>
            {loading ? 'Loading…' : `${eventCount} ${eventCount === 1 ? 'event' : 'events'} on file`}
          </p>
        </section>

        {/* Loading / Error */}
        {loading && (
          <div className="text-center py-12" data-testid="vlog-loading">
            <Loader2 size={26} className="animate-spin mx-auto" style={{ color: SAGE }} />
          </div>
        )}
        {error && !loading && (
          <div
            className="rounded-xl p-5"
            style={{ background: '#FFFBF1', border: '1px solid #F5E5B8', color: '#6B5500' }}
            data-testid="vlog-error"
          >
            <strong>Could not load validation log:</strong> {error}
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && events.length === 0 && (
          <div
            className="rounded-2xl p-8 text-center"
            style={{ background: '#FAFBF9', border: '1px dashed #BCC5B5' }}
            data-testid="vlog-empty"
          >
            <Inbox size={28} style={{ color: SAGE_MUTED }} className="mx-auto mb-3" />
            <h3 style={{ fontFamily: FONT_HEADING, fontSize: 22, color: INK, marginBottom: 6, fontWeight: 400 }}>
              No events match this filter
            </h3>
            <p style={{ fontSize: 13.5, color: SAGE_MUTED }}>
              Try a different filter — events are added as they happen.
            </p>
          </div>
        )}

        {/* Timeline */}
        {!loading && !error && events.length > 0 && (
          <section data-testid="vlog-timeline" style={{ position: 'relative' }}>
            {/* Vertical connector line */}
            <div
              style={{
                position: 'absolute',
                left: 19,
                top: 8,
                bottom: 8,
                width: 1.5,
                background: 'linear-gradient(to bottom, rgba(45,106,79,0.0), rgba(45,106,79,0.25) 8%, rgba(45,106,79,0.25) 92%, rgba(45,106,79,0.0))',
                pointerEvents: 'none',
              }}
            />

            {Object.entries(groupedByDate).map(([date, dayEvents]) => (
              <div key={date} style={{ marginBottom: 28 }}>
                {/* Date header */}
                <div
                  className="flex items-center gap-2 mb-4 pl-12"
                  data-testid={`vlog-date-${date.replace(/[^a-zA-Z0-9]+/g, '-')}`}
                >
                  <Calendar size={12} style={{ color: SAGE_MUTED }} />
                  <span style={{ fontSize: 11, color: SAGE_MUTED, textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 600 }}>
                    {date}
                  </span>
                </div>

                {/* Day events */}
                {dayEvents.map((e) => {
                  const cfg = EVENT_TYPE_CONFIG[e.event_type] || EVENT_TYPE_CONFIG.announcement;
                  const Icon = cfg.icon;
                  return (
                    <article
                      key={e.id || e.timestamp}
                      className="rounded-xl p-5 mb-3 ml-12 relative"
                      style={{
                        background: PAPER,
                        border: '1px solid rgba(31, 58, 46, 0.08)',
                        boxShadow: '0 1px 2px rgba(20, 30, 15, 0.04)',
                      }}
                      data-testid={`vlog-event-${e.id}`}
                    >
                      {/* Timeline node */}
                      <div
                        className="inline-flex items-center justify-center"
                        style={{
                          position: 'absolute',
                          left: -34,
                          top: 18,
                          width: 28, height: 28, borderRadius: '50%',
                          background: cfg.bg, border: `2px solid ${cfg.color}`,
                        }}
                      >
                        <Icon size={13} style={{ color: cfg.color }} strokeWidth={2.2} />
                      </div>

                      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                        <span
                          style={{
                            fontSize: 10.5, fontWeight: 700,
                            color: cfg.color, background: cfg.bg,
                            border: `1px solid ${cfg.border}`,
                            borderRadius: 999, padding: '3px 9px',
                            textTransform: 'uppercase', letterSpacing: '0.08em',
                          }}
                        >
                          {cfg.label}
                        </span>
                        <span style={{ fontSize: 11.5, color: SAGE_MUTED, fontVariantNumeric: 'tabular-nums' }}>
                          {fmtTime(e.timestamp)}
                        </span>
                      </div>

                      <h3
                        style={{
                          fontFamily: FONT_HEADING, fontSize: 19, color: INK,
                          fontWeight: 500, lineHeight: 1.3, marginBottom: 6, fontStyle: 'italic',
                        }}
                      >
                        {e.title}
                      </h3>
                      <p style={{ fontSize: 13.5, lineHeight: 1.65, color: CHARCOAL, fontWeight: 300, marginBottom: 12 }}>
                        {e.body}
                      </p>

                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <span style={{ fontSize: 11.5, color: SAGE_MUTED, fontStyle: 'italic' }}>
                          — {e.author}
                          {e.module && <span style={{ marginLeft: 8, color: SAGE, fontStyle: 'normal', fontWeight: 600 }}>· {e.module}</span>}
                        </span>
                        {e.links && e.links.length > 0 && (
                          <div className="flex items-center gap-3 flex-wrap">
                            {e.links.map((lnk, i) => (
                              <Link
                                key={i}
                                to={lnk.url}
                                style={{
                                  fontSize: 12, color: SAGE, fontWeight: 600,
                                  textDecoration: 'none', borderBottom: `1px solid ${SAGE}30`,
                                  display: 'inline-flex', alignItems: 'center', gap: 4,
                                }}
                                {...(lnk.url.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                              >
                                {lnk.label}
                                {lnk.url.startsWith('http') && <ExternalLink size={10} />}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            ))}
          </section>
        )}

        {/* Footer note */}
        {!loading && !error && data?.note && (
          <div
            className="text-center pt-10 mt-10"
            style={{ borderTop: '1px solid rgba(31, 58, 46, 0.08)' }}
            data-testid="vlog-footer-note"
          >
            <p style={{ fontFamily: FONT_HEADING, fontStyle: 'italic', fontSize: 14, color: SAGE_MUTED, lineHeight: 1.6, maxWidth: 600, margin: '0 auto' }}>
              {data.note}
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

const navLinkStyle = {
  fontSize: 13, color: '#2C2C2C', textDecoration: 'none', fontWeight: 500,
};
